import type { Session } from '@supabase/supabase-js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getSupabase, isSupabaseConfigured } from '../../lib/supabase/client'
import { getAccessState, getMfaState, getSession, logout as signOut } from '../../features/auth/api'
import type { AccessState } from '../../types/domain'
import { AuthContext } from './auth-context'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [access, setAccess] = useState<AccessState | null>(null)
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [error, setError] = useState<Error | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)

  const refreshMfa = useCallback(async () => {
    if (!session) {
      setMfaRequired(false)
      return
    }
    const state = await getMfaState()
    setMfaRequired(state.required)
  }, [session])

  const refreshAccess = useCallback(async () => {
    if (!session) {
      setAccess(null)
      return
    }
    try {
      setAccess(await getAccessState())
      setError(null)
    } catch (nextError) {
      setAccess(null)
      setError(nextError as Error)
    }
  }, [session])

  useEffect(() => {
    if (!isSupabaseConfigured) return
    let active = true
    void getSession()
      .then(async (initialSession) => {
        if (!active) return
        setSession(initialSession)
        if (initialSession) {
          const [nextAccess, mfa] = await Promise.all([getAccessState(), getMfaState()])
          setAccess(nextAccess)
          setMfaRequired(mfa.required)
        }
      })
      .catch((nextError) => active && setError(nextError as Error))
      .finally(() => active && setLoading(false))
    const { data } = getSupabase().auth.onAuthStateChange((_event, nextSession) => {
      setLoading(Boolean(nextSession))
      setSession(nextSession)
      if (!nextSession) {
        setAccess(null)
        return
      }
      queueMicrotask(() => {
        void Promise.all([getAccessState(), getMfaState()])
          .then(([nextAccess, mfa]) => {
            setAccess(nextAccess)
            setMfaRequired(mfa.required)
          })
          .catch((nextError) => setError(nextError as Error))
          .finally(() => setLoading(false))
      })
    })
    return () => {
      active = false
      data.subscription.unsubscribe()
    }
  }, [])

  const logout = useCallback(async () => {
    await signOut()
    setAccess(null)
    setMfaRequired(false)
  }, [])

  const value = useMemo(
    () => ({ session, access, loading, error, mfaRequired, refreshAccess, refreshMfa, logout }),
    [session, access, loading, error, mfaRequired, refreshAccess, refreshMfa, logout],
  )
  return <AuthContext value={value}>{children}</AuthContext>
}
